/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type PropsWithChildren, useEffect } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { PrimaryProductKey, ProductKeys } from '../../constants/products';
import { serveJiraNinFpsNavigation } from '../../utils/experiments/experiment-exports';
import { useAppContext } from '../app-context';

type BodyProps = PropsWithChildren<{
	bodyRef: React.RefObject<HTMLDivElement>;
	mainContentRef: React.RefObject<HTMLDivElement>;
	isLoading?: boolean;
	rightPanel?: JSX.Element;
	selectedProduct?: ProductKeys;
	primaryProduct?: PrimaryProductKey;
	setMainContentWidth: React.Dispatch<React.SetStateAction<number | undefined>>;
	sideBySide?: boolean;
	fullWidthSainPortalRef?: React.RefObject<HTMLDivElement>;
}>;

const styles = cssMap({
	container: {
		overflowY: 'scroll',
	},
	contentContainer: {
		margin: 'auto',
		maxWidth: '100%',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		alignItems: 'space-between',
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	contentContainerWithTabs: {
		margin: 'auto',
		maxWidth: '100%',
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		alignItems: 'space-between',
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	mainContent: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'stretch',
		gap: token('space.200'),
		paddingBlock: token('space.250'),
		flexGrow: 1,
		maxWidth: '768px',
		zIndex: 0,
	},
	mainContentCentered: {
		marginLeft: 'auto',
		marginRight: 'auto',
	},
	rightPanelWrapper: {
		display: 'none',
		'@media (min-width: 48rem)': {
			display: 'block',
		},
	},
	rightPanel: {
		position: 'sticky',
		top: 0,
		width: '17rem',
	},
	fullWidthSainPortal: {
		margin: 'auto',
		maxWidth: '100%',
		paddingTop: token('space.250'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
		'&:empty': {
			display: 'none',
		},
	},
});

export const Body: ({
	selectedProduct,
	rightPanel,
	children,
	bodyRef,
	mainContentRef,
	setMainContentWidth,
	primaryProduct,
	sideBySide,
	fullWidthSainPortalRef,
}: BodyProps) => JSX.Element = ({
	selectedProduct,
	rightPanel,
	children,
	bodyRef,
	mainContentRef,
	setMainContentWidth,
	primaryProduct,
	sideBySide,
	fullWidthSainPortalRef,
}: BodyProps) => {
	const { isNav4Enabled } = useAppContext();
	const { locale } = useIntl();
	const fpsNinNavigation = serveJiraNinFpsNavigation({
		primaryProduct,
		fireLayerExposure: false,
		locale,
	});

	useEffect(() => {
		bodyRef?.current?.scrollTo?.(0, 0);
	}, [selectedProduct, bodyRef, isNav4Enabled]);

	useEffect(() => {
		const callback = () => {
			if (mainContentRef.current) {
				setMainContentWidth(mainContentRef.current.offsetWidth);
			}
		};

		const resizeObserver = new ResizeObserver(callback);

		if (mainContentRef.current) {
			resizeObserver.observe(mainContentRef.current);
			// Set initial width
			callback();
		}

		return () => {
			resizeObserver.disconnect();
		};
		// These dependencies should never change
	}, [mainContentRef, setMainContentWidth]);

	return (
		<div css={styles.container} ref={bodyRef} data-testid="search-page-body">
			{fullWidthSainPortalRef && (
				<div
					css={styles.fullWidthSainPortal}
					ref={fullWidthSainPortalRef}
					data-testid="search-page-full-width-sain-portal"
				/>
			)}
			<Inline
				space="space.800"
				xcss={
					fpsNinNavigation?.cohort === 'tabs'
						? styles.contentContainerWithTabs
						: styles.contentContainer
				}
			>
				{/* todo replacement component for search page lite */}
				<div
					css={[styles.mainContent, !rightPanel && styles.mainContentCentered]}
					ref={mainContentRef}
					data-testid="search-page-body-main-content"
				>
					{children}
				</div>
				{!sideBySide && rightPanel && (
					<Box xcss={styles.rightPanelWrapper}>
						<div css={styles.rightPanel}>{rightPanel}</div>
					</Box>
				)}
			</Inline>
		</div>
	);
};
